import {
  bySubstance,
  hero,
  heroCss,
  joinLabels,
  pageLabel,
  panel,
  panelCss,
  rankMarker,
  rankMarkerCss,
  readingTime,
} from '../armKit';
import { cardPartCss, collapsedRowCss, compactCardsCss } from '../surface';

import type { ArmContext, ArmSpec } from '../armRegistry';
import type { WorkFacts } from '../pageData';
import type { ProfileCard } from '../surface';

type Facts = Map<number, WorkFacts>;
type Lead = { card: ProfileCard; url: string };

const FLAG = 'leadindex';
const HERO = 'hero';
// A second, constant-valued flag for the index rows: FLAG carries each row's
// own rank, and a rule keyed on a per-card value cannot cover every row.
const ROW_FLAG = 'leadrow';
const FIRST_INDEX_RANK = 2;
const LABEL = 'Start with this paper';
const ACTION = 'Read the paper';
const READ_SOURCE = 'profile-liautotest-lead-read';

function pagesOf(facts: Facts, workId: number): number | null | undefined {
  return facts.get(workId)?.pageCount;
}

function rankOf(card: ProfileCard): string | null {
  return card.flag(FLAG);
}

function leadOf(cards: ProfileCard[]): Lead | null {
  const card = cards.find(
    (candidate) => candidate.hasHiddenAbstract && candidate.url != null,
  );

  return card?.url == null ? null : { card, url: card.url };
}

function heroFor(
  { card, url }: Lead,
  facts: Facts,
  ctx: ArmContext,
): HTMLElement {
  const { root, body } = panel({ title: LABEL });

  const pages = pagesOf(facts, card.workId);
  const meta = joinLabels([pageLabel(pages), readingTime(pages)]);
  const track = (): void => ctx.track(READ_SOURCE, { entityId: card.workId });

  hero(body, {
    title: card.title,
    href: url,
    onTitleClick: track,
    meta: meta === '' ? undefined : meta,
    abstract: card.abstractText(),
    actions: [{ label: ACTION, href: url, onClick: track }],
  });

  return root;
}

// The index rows are keyed off the per-card flag rather than off the card
// class, so a batch the observer has not reached yet keeps its full card
// styling instead of flashing as a half-built row.
function indexCards(
  cards: ProfileCard[],
  facts: Facts,
  from: number,
  ctx: ArmContext,
): void {
  cards
    .filter((card) => rankOf(card) == null)
    .forEach((card, offset) => {
      const rank = String(from + offset);

      card.collapse();
      card.setFlag(FLAG, rank);
      const rowScope = `${ctx.scope} ${card.setFlag(ROW_FLAG)}`;
      ctx.injectStyles(
        compactCardsCss(rowScope, { itemGap: '8px' }) +
          collapsedRowCss(rowScope) +
          cardPartCss('row', rowScope, 'align-items: center;') +
          cardPartCss('body', rowScope, 'margin-bottom: 0 !important;') +
          cardPartCss('title', rowScope, 'margin-bottom: 0 !important;'),
      );

      card.prependToTitle(rankMarker(`${rank}.`));

      const pages = pageLabel(pagesOf(facts, card.workId));
      if (pages != null) card.addChip(pages);
    });
}

function indexedCount(cards: ProfileCard[]): number {
  return cards.filter((card) => {
    const rank = rankOf(card);

    return rank != null && rank !== HERO;
  }).length;
}

export const leadPaperAndIndex: ArmSpec = {
  onReady: async (ctx) => {
    const facts = await ctx.workFacts();
    if (facts.size === 0) return;

    const compare = bySubstance((workId) => pagesOf(facts, workId));
    const lead = leadOf([...ctx.cards()].sort(compare));
    if (lead == null) return;

    // Last gate before the first mutation: a profile with no list cannot
    // carry the hero, and half the arm is worse than control.
    const slot = ctx.mountSlot('listHero');
    if (slot == null) return;

    ctx.injectStyles(
      panelCss(ctx.scope) +
        heroCss(ctx.scope) +
        rankMarkerCss(ctx.scope, {
          minWidth: '32px',
          fontSize: '20px',
          marginRight: '6px',
          narrowMinWidth: '26px',
        }),
    );
    ctx.reorderCards(compare, 'list');

    // Flagged and hidden, so the lead degrades to no lead at all rather than
    // a second copy of the hero if the arm's stylesheet is lost.
    lead.card.setFlag(FLAG, HERO);
    lead.card.hide();
    slot.appendChild(heroFor(lead, facts, ctx));

    indexCards(ctx.cards(), facts, FIRST_INDEX_RANK, ctx);
  },

  // Gated on the hero the ranking pass left rather than on a fresh read, so a
  // late batch cannot engage an arm that degraded to control. No reorder and
  // no second hero: the ranking the visitor is reading must not move under
  // them.
  onCards: async (cards, ctx) => {
    const facts = await ctx.workFacts();
    const all = ctx.cards();
    if (!all.some((card) => rankOf(card) === HERO)) return;

    indexCards(cards, facts, indexedCount(all) + FIRST_INDEX_RANK, ctx);
  },
};
